package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.LayoutDirection
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Shimmer/skeleton loading primitives — THE loading treatment for content surfaces (spinners stay
 * for button-level busy states only). A skeleton mirrors the layout it replaces so the page doesn't
 * jump when data lands: compose these primitives in the same arrangement as the real content.
 *
 * All colors/sizes/shapes are tokens; the highlight sweep respects RTL (travels start→end).
 */
private const val SHIMMER_DURATION_MS = 1200

/** Animated shimmer brush shared by every skeleton primitive in the composition. */
@Composable
private fun shimmerBrush(): Brush {
    val transition = rememberInfiniteTransition(label = "skeleton-shimmer")
    val progress by transition.animateFloat(
        initialValue = -1f,
        targetValue = 2f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = SHIMMER_DURATION_MS, easing = LinearEasing),
            repeatMode = RepeatMode.Restart,
        ),
        label = "skeleton-shimmer-progress",
    )
    val base = AppTheme.colors.surfaceContainerHigh
    val highlight = AppTheme.colors.surface
    // Sweep toward the reading direction so RTL doesn't feel like it loads "backwards".
    val rtl = LocalLayoutDirection.current == LayoutDirection.Rtl
    val band = 600f
    val startX = if (rtl) (1f - progress) * band else progress * band
    return Brush.linearGradient(
        colors = listOf(base, highlight, base),
        start = Offset(startX, 0f),
        end = Offset(startX + band / 2, band / 4),
    )
}

/** Rectangular shimmer block — building block for card/image placeholders. */
@Composable
fun SkeletonBox(
    modifier: Modifier = Modifier,
    shape: Shape = AppTheme.shapes.card,
) {
    Box(modifier = modifier.background(shimmerBrush(), shape))
}

/** One line of "text": a pill-shaped shimmer. [width] null = fill the available width. */
@Composable
fun SkeletonLine(
    modifier: Modifier = Modifier,
    width: Dp? = null,
    height: Dp = AppTheme.sizes.skeletonLine,
) {
    val sized = if (width != null) modifier.width(width) else modifier.fillMaxWidth()
    Box(modifier = sized.height(height).background(shimmerBrush(), AppTheme.shapes.pill))
}

/** Circular shimmer — avatar/icon placeholder. */
@Composable
fun SkeletonCircle(size: Dp, modifier: Modifier = Modifier) {
    Box(modifier = modifier.size(size).background(shimmerBrush(), AppTheme.shapes.pill))
}

/** Placeholder for a [SectionHeader] title line, so headers don't render over empty sections. */
@Composable
fun SectionHeaderSkeleton(modifier: Modifier = Modifier) {
    SkeletonLine(
        modifier = modifier,
        width = AppTheme.sizes.featuredCardWidth / 2,
        height = AppTheme.sizes.skeletonTitle,
    )
}

/** Skeleton mirroring [FeaturedBusinessCard] / [NewsCard]: fixed-width cover + two text lines. */
@Composable
fun FeaturedCardSkeleton(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.width(AppTheme.sizes.featuredCardWidth),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        SkeletonBox(modifier = Modifier.fillMaxWidth().aspectRatio(16f / 10f))
        SkeletonLine(width = AppTheme.sizes.featuredCardWidth * 3 / 4, height = AppTheme.sizes.skeletonTitle)
        SkeletonLine(width = AppTheme.sizes.featuredCardWidth / 2)
    }
}

/** Skeleton mirroring a business/list row: thumbnail + two lines. */
@Composable
fun ListItemSkeleton(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SkeletonBox(
            modifier = Modifier.size(AppTheme.sizes.listThumbnail),
            shape = AppTheme.shapes.card,
        )
        Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
            SkeletonLine(width = AppTheme.sizes.featuredCardWidth * 3 / 4, height = AppTheme.sizes.skeletonTitle)
            SkeletonLine(width = AppTheme.sizes.featuredCardWidth / 2)
        }
    }
}

/** Skeleton mirroring [CategoryTile]: rounded tile + short caption line. */
@Composable
fun CategoryTileSkeleton(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        SkeletonBox(
            modifier = Modifier.size(AppTheme.sizes.homeCategoryTile),
            shape = AppTheme.shapes.card,
        )
        SkeletonLine(width = AppTheme.sizes.homeCategoryTile)
    }
}

/** Skeleton mirroring a feed post card: avatar + author lines, then body lines. */
@Composable
fun FeedPostSkeleton(modifier: Modifier = Modifier) {
    AppCard(modifier = modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SkeletonCircle(size = AppTheme.sizes.avatarMd)
                Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                    SkeletonLine(width = AppTheme.sizes.featuredCardWidth / 2, height = AppTheme.sizes.skeletonTitle)
                    SkeletonLine(width = AppTheme.sizes.featuredCardWidth / 3)
                }
            }
            SkeletonLine()
            SkeletonLine(width = AppTheme.sizes.featuredCardWidth * 3 / 4)
        }
    }
}

/** Skeleton mirroring a full-width media card (event/marketplace detail rows): cover + lines. */
@Composable
fun MediaCardSkeleton(
    modifier: Modifier = Modifier,
    coverAspectRatio: Float = 16f / 9f,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        SkeletonBox(modifier = Modifier.fillMaxWidth().aspectRatio(coverAspectRatio))
        SkeletonLine(width = AppTheme.sizes.featuredCardWidth * 3 / 4, height = AppTheme.sizes.skeletonTitle)
        SkeletonLine(width = AppTheme.sizes.featuredCardWidth / 2)
    }
}
